import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  PlusCircle,
  Zap,
  Users,
  DollarSign,
  Clock,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dealService } from '@/services/deal.service';
import { cn } from '@/lib/utils';
import { OnboardingTour } from '@/components/OnboardingTour';

export function DashboardPage() {
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('merchant_onboarding_completed'),
  );

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dealService.getDashboardStats,
    refetchInterval: 30_000, // Refresh every 30s
  });

  return (
    <div>
      {showOnboarding && (
        <OnboardingTour onComplete={() => setShowOnboarding(false)} />
      )}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            Welcome back, {user?.displayName || 'Merchant'}!
          </h1>
          <p className="mt-1 text-muted-foreground">
            Here's what's happening with your deals today.
          </p>
        </div>
        <Link to="/deals/create">
          <Button>
            <PlusCircle className="mr-2 h-4 w-4" />
            Create New Deal
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Deals"
          value={isLoading ? '--' : String(stats?.activeDeals ?? 0)}
          icon={Zap}
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
        />
        <StatCard
          title="Today's Claims"
          value={isLoading ? '--' : String(stats?.todayClaims ?? 0)}
          icon={Users}
          iconColor="text-green-600"
          iconBg="bg-green-100"
        />
        <StatCard
          title="Today's Revenue"
          value={isLoading ? '--' : `${(stats?.todayRevenue ?? 0).toFixed(2)} AZN`}
          icon={DollarSign}
          iconColor="text-orange-600"
          iconBg="bg-orange-100"
        />
        <StatCard
          title="Avg Arrival Time"
          value={
            isLoading
              ? '--'
              : stats?.avgArrivalTime
                ? `${Math.round(stats.avgArrivalTime / 60)} min`
                : '--'
          }
          icon={Clock}
          iconColor="text-purple-600"
          iconBg="bg-purple-100"
        />
      </div>

      {/* Quick Actions + Recent Activity */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link
              to="/deals/create"
              className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <PlusCircle className="h-5 w-5 text-primary" />
                <span className="font-medium">Create a New Deal</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link
              to="/deals/active"
              className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-blue-500" />
                <span className="font-medium">View Active Deals</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link
              to="/analytics"
              className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-green-500" />
                <span className="font-medium">View Analytics</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner className="h-6 w-6" />
              </div>
            ) : stats?.recentActivity && stats.recentActivity.length > 0 ? (
              <div className="space-y-3">
                {stats.recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold',
                        activity.type === 'claim'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      )}
                    >
                      {activity.discount}%
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{activity.dealName}</p>
                      <p className="text-xs text-muted-foreground">
                        Claimed by {activity.customerName}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(activity.timestamp), 'h:mm a')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-40 flex-col items-center justify-center text-center">
                <p className="text-muted-foreground">No recent activity</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Claims will appear here in real time
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBg,
}: {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  iconBg: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={cn('rounded-lg p-2', iconBg)}>
            <Icon className={cn('h-5 w-5', iconColor)} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
